import { isDateKey, isMonthKey, type DateKey, type MonthKey } from '@kff/engine';

/**
 * A project's delivery spec (owner 2026-11-01, thread "Matcha Mondays projektdetaljer i Hubben"): what Monday's Video
 * Management board kept per project that the Hub had nowhere to put – formats, channels, subtitles, paid or organic,
 * the delivery time, number and length of videos, what the film contains, plus the planning meeting, a project running
 * over several months and the client's reference for the invoice. Hub projects edit it in the "Leverans" box on Details
 * (each field saves on its own); Monday projects get it from the sync, read only.
 *
 * Every field is optional: null / [] is "not set". Fixed choices are keys with labels below; formats, channels and
 * subtitle languages also take a few short custom entries ("+ annat").
 */
export interface DeliverySpec {
  /** "1 film à 90 s + 6 korta klipp" */
  lengths: string | null;
  formats: string[];
  channels: string[];
  /** null: not set; []: no subtitles; else the languages */
  subtitles: string[] | null;
  advertising: Advertising | null;
  /** working days to the first draft; null: as the client's agreement says */
  deliveryDays: number | null;
  includes: string[];
  planningDate: DateKey | null;
  /** last month of a project that runs over several months */
  untilMonth: MonthKey | null;
  /** the client's reference or PO for the invoice */
  reference: string | null;
}

export const FORMATS = ['16:9', '9:16', '1:1', '4:5', '4:3', '1.91:1'] as const;
export const CHANNELS = ['web', 'instagram', 'youtube', 'linkedin', 'facebook', 'tiktok', 'tv', 'fair'] as const;
export const SUBTITLE_LANGS = ['sv', 'en', 'no', 'da', 'fi', 'de', 'pl', 'fr', 'ar'] as const;
export const INCLUDES = ['photo', 'thumbnail', 'graphics', 'animation', 'ai', 'actor', 'voice'] as const;
export const ADVERTISING = ['organic', 'paid', 'both'] as const;
export type Advertising = (typeof ADVERTISING)[number];
/** the chips; any other whole number of days is "Annat" */
export const DELIVERY_DAY_CHOICES = [1, 5, 10, 12] as const;
/** the first draft when neither the project nor the agreement says (the Hub quote's own terms: five working days) */
export const STANDARD_DELIVERY_DAYS = 5;
export const MAX_DELIVERY_DAYS = 60;

type L = { sv: string; en: string };
export const SPEC_LABELS: {
  channels: Record<(typeof CHANNELS)[number], L>;
  subtitles: Record<(typeof SUBTITLE_LANGS)[number], L>;
  includes: Record<(typeof INCLUDES)[number], L>;
  advertising: Record<Advertising, L>;
} = {
  channels: {
    web: { sv: 'Webb', en: 'Web' },
    instagram: { sv: 'Instagram', en: 'Instagram' },
    youtube: { sv: 'YouTube', en: 'YouTube' },
    linkedin: { sv: 'LinkedIn', en: 'LinkedIn' },
    facebook: { sv: 'Facebook', en: 'Facebook' },
    tiktok: { sv: 'TikTok', en: 'TikTok' },
    tv: { sv: 'TV/streaming', en: 'TV/streaming' },
    fair: { sv: 'Mässa', en: 'Trade fair' }
  },
  subtitles: {
    sv: { sv: 'Svenska', en: 'Swedish' },
    en: { sv: 'Engelska', en: 'English' },
    no: { sv: 'Norska', en: 'Norwegian' },
    da: { sv: 'Danska', en: 'Danish' },
    fi: { sv: 'Finska', en: 'Finnish' },
    de: { sv: 'Tyska', en: 'German' },
    pl: { sv: 'Polska', en: 'Polish' },
    fr: { sv: 'Franska', en: 'French' },
    ar: { sv: 'Arabiska', en: 'Arabic' }
  },
  includes: {
    photo: { sv: 'Foto', en: 'Photos' },
    thumbnail: { sv: 'Thumbnail', en: 'Thumbnail' },
    graphics: { sv: 'Grafik', en: 'Graphics' },
    animation: { sv: 'Animation', en: 'Animation' },
    ai: { sv: 'AI', en: 'AI' },
    actor: { sv: 'Skådespelare', en: 'Actor' },
    voice: { sv: 'Voice-over', en: 'Voice-over' }
  },
  advertising: {
    organic: { sv: 'Organiskt', en: 'Organic' },
    paid: { sv: 'Betald annonsering', en: 'Paid ads' },
    both: { sv: 'Båda', en: 'Both' }
  }
};

/** A chip's label: the fixed key's label, or the custom entry as typed. */
export function specLabel(kind: 'channels' | 'subtitles' | 'includes', key: string, lang: 'sv' | 'en'): string {
  const t = (SPEC_LABELS[kind] as Record<string, L>)[key];
  return t ? t[lang] : key;
}

export const emptySpec = (): DeliverySpec => ({
  lengths: null,
  formats: [],
  channels: [],
  subtitles: null,
  advertising: null,
  deliveryDays: null,
  includes: [],
  planningDate: null,
  untilMonth: null,
  reference: null
});

export const SPEC_KEYS = ['lengths', 'formats', 'channels', 'subtitles', 'advertising', 'deliveryDays', 'includes', 'planningDate', 'untilMonth', 'reference'] as const satisfies readonly (keyof DeliverySpec)[];
export type SpecKey = (typeof SPEC_KEYS)[number];

export class SpecError extends Error {
  constructor(
    readonly field: SpecKey,
    message: string
  ) {
    super(message);
  }
}

const MAX_CUSTOM = 40;
const MAX_LIST = 12;
const text = (v: unknown, max: number): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(/\s+/g, ' ');
  return s ? s.slice(0, max) : null;
};
/** A list of keys: the fixed ones as they are (case-insensitive), custom entries trimmed; no duplicates. */
function list(v: unknown, fixed: readonly string[], field: SpecKey, custom: boolean): string[] {
  if (v === null || v === undefined) return [];
  if (!Array.isArray(v)) throw new SpecError(field, 'Expected a list.');
  const out: string[] = [];
  for (const x of v) {
    const s = text(x, MAX_CUSTOM);
    if (!s) continue;
    const key = fixed.find((f) => f.toLowerCase() === s.toLowerCase()) ?? (custom ? s : null);
    if (!key) throw new SpecError(field, `Not a choice: ${s}`);
    if (!out.some((o) => o.toLowerCase() === key.toLowerCase())) out.push(key);
  }
  if (out.length > MAX_LIST) throw new SpecError(field, `At most ${MAX_LIST}.`);
  return out;
}

/** One field from the web or the sync into its clean value; throws SpecError on a value that is not allowed. */
export function cleanSpecField<K extends SpecKey>(key: K, v: unknown): DeliverySpec[K] {
  const r = ((): unknown => {
    switch (key) {
      case 'lengths':
        return text(v, 300);
      case 'reference':
        return text(v, 100);
      case 'formats':
        return list(v, FORMATS, key, true);
      case 'channels':
        return list(v, CHANNELS, key, true);
      case 'includes':
        return list(v, INCLUDES, key, false);
      case 'subtitles':
        return v === null || v === undefined ? null : list(v, SUBTITLE_LANGS, key, true);
      case 'advertising':
        if (v === null || v === undefined || v === '') return null;
        if (!ADVERTISING.includes(v as Advertising)) throw new SpecError(key, 'Organic, paid or both.');
        return v;
      case 'deliveryDays': {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        if (!Number.isInteger(n) || n < 1 || n > MAX_DELIVERY_DAYS) throw new SpecError(key, `1–${MAX_DELIVERY_DAYS} working days.`);
        return n;
      }
      case 'planningDate':
        if (v === null || v === undefined || v === '') return null;
        if (!isDateKey(v)) throw new SpecError(key, 'A date: YYYY-MM-DD.');
        return v;
      case 'untilMonth':
        if (v === null || v === undefined || v === '') return null;
        if (!isMonthKey(v)) throw new SpecError(key, 'A month: YYYY-MM.');
        return v;
    }
  })();
  return r as DeliverySpec[K];
}

/** A stored spec (jsonb, maybe from an older version) into a whole spec; values that no longer validate are dropped. */
export function readSpec(raw: unknown): DeliverySpec {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const s = emptySpec();
  for (const k of SPEC_KEYS) {
    try {
      (s as unknown as Record<string, unknown>)[k] = cleanSpecField(k, o[k]);
    } catch {
      /* keep the empty value */
    }
  }
  return s;
}

/** True when nothing is set (the Details box then shows only the fields to fill in). */
export const specIsEmpty = (s: DeliverySpec): boolean => SPEC_KEYS.every((k) => (Array.isArray(s[k]) ? (s[k] as unknown[]).length === 0 : s[k] === null));

/** What a new project for the client takes from the client's latest one: the things that are usually the same. */
export const INHERITED_SPEC_KEYS = ['formats', 'channels', 'subtitles', 'advertising', 'reference'] as const satisfies readonly SpecKey[];
export function inheritedSpec(latest: DeliverySpec | null | undefined): Partial<DeliverySpec> {
  if (!latest) return {};
  const out: Partial<DeliverySpec> = {};
  for (const k of INHERITED_SPEC_KEYS) {
    const v = latest[k];
    // subtitles [] is a choice ("Nej"); an empty list of formats or channels is not
    if (k === 'subtitles' ? v !== null : Array.isArray(v) ? v.length : v !== null) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/**
 * The delivery time that applies to a project: its own, else the client's agreement in force, else the standard (owner
 * 2026-11-01: "Leveranstid bör utgå från standard som står i avtalet"; older agreements often promise longer).
 */
export function deliveryDaysOf(spec: Pick<DeliverySpec, 'deliveryDays'> | null | undefined, agreementDays: number | null | undefined): { days: number; source: 'project' | 'agreement' | 'standard' } {
  if (spec?.deliveryDays) return { days: spec.deliveryDays, source: 'project' };
  if (agreementDays) return { days: agreementDays, source: 'agreement' };
  return { days: STANDARD_DELIVERY_DAYS, source: 'standard' };
}

// ---- Monday's columns (Video Management) into a spec ----

/** The raw labels of the spec columns on a Monday project, as the sync reads them. */
export interface MondaySpecRaw {
  /** Delivery `status0`: "5d", "10d", "12d", "2d", "Exp 5d", "Dun 2d" */
  delivery?: string | null;
  /** Advertising `status3`: Organic / Paid ads / Both */
  advertising?: string | null;
  /** Platforms `text3` (dropdown): "(16:9) Webb, (9:16) Instagram story" */
  platforms?: string | null;
  /** Number and length of video `mirror71` */
  lengths?: string | null;
  /** Subtitles `text7`: Subtitles / No / SRT */
  subtitles?: string | null;
  /** Subtitles `dropdown4`: "Swedish, English" */
  subtitleLangs?: string | null;
  /** Photos `text6`: No / Photography / Thumbnail */
  photos?: string | null;
  /** Animation/Graphics `text27`: No / Animation / Graphic / AI */
  graphics?: string | null;
  /** Actor/VO `text08`: No / Actor / VO / Both / AI */
  actorVo?: string | null;
  /** Planning `date46` */
  planning?: string | null;
  /** Double/Deviant `dropdown_mm5ww25b`: "Sep-Oct-Nov" */
  period?: string | null;
}

const CHANNEL_WORDS: [RegExp, (typeof CHANNELS)[number]][] = [
  [/\bweb+\b|\bwebb\b/i, 'web'],
  [/instagram/i, 'instagram'],
  [/youtube|shorts/i, 'youtube'],
  [/linkedin/i, 'linkedin'],
  [/facebook|meta|news feed/i, 'facebook'],
  [/tiktok/i, 'tiktok'],
  [/tv|play|streaming|film\/tv/i, 'tv'],
  [/mässa/i, 'fair']
];
const LANG_WORDS: [RegExp, (typeof SUBTITLE_LANGS)[number]][] = [
  [/swedish|svenska/i, 'sv'],
  [/english|engelska/i, 'en'],
  [/norwegian|norska/i, 'no'],
  [/danish|danska/i, 'da'],
  [/finish|finnish|finska/i, 'fi'],
  [/german|tyska/i, 'de'],
  [/polish|polska/i, 'pl'],
  [/french|franska/i, 'fr'],
  [/arab/i, 'ar']
];
const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, maj: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, okt: 10, nov: 11, dec: 12 };

/** The last month a "Sep-Oct-Nov" period reaches, counted from the project's month (a period runs forward, over new year). */
export function untilMonthOf(period: string, from: MonthKey): MonthKey | null {
  const parts = period
    .split(/[-–,\s]+/)
    .map((p) => MONTHS[p.trim().toLowerCase().slice(0, 3)])
    .filter((n): n is number => Boolean(n));
  const last = parts[parts.length - 1];
  if (!last || parts.length < 2) return null;
  const y = Number(from.slice(0, 4));
  const m = Number(from.slice(5, 7));
  const year = last >= m ? y : y + 1;
  const out = `${year}-${String(last).padStart(2, '0')}` as MonthKey;
  return out > from ? out : null;
}

/** Monday's labels into a spec. Blank and "No" mean not set or none; unknown platform text is kept as a custom channel. */
export function specFromMonday(raw: MondaySpecRaw | null | undefined, month: MonthKey): DeliverySpec {
  const s = emptySpec();
  if (!raw) return s;
  const t = (v: string | null | undefined) => (v ?? '').trim();
  const days = /(\d+)\s*d/i.exec(t(raw.delivery));
  if (days) s.deliveryDays = Math.min(MAX_DELIVERY_DAYS, Math.max(1, Number(days[1])));
  const ad = t(raw.advertising).toLowerCase();
  s.advertising = ad === 'organic' ? 'organic' : ad.startsWith('paid') ? 'paid' : ad === 'both' ? 'both' : null;
  const plat = t(raw.platforms);
  if (plat) {
    for (const m of plat.matchAll(/\(?\s*(\d+(?:\.\d+)?)\s*[:x]\s*(\d+)\s*\)?/gi)) {
      const f = `${m[1]}:${m[2]}`;
      if (!s.formats.includes(f)) s.formats.push(f);
    }
    for (const [re, key] of CHANNEL_WORDS) if (re.test(plat) && !s.channels.includes(key)) s.channels.push(key);
  }
  s.lengths = t(raw.lengths) ? t(raw.lengths).slice(0, 300) : null;
  const sub = t(raw.subtitles).toLowerCase();
  const langs = LANG_WORDS.filter(([re]) => re.test(t(raw.subtitleLangs))).map(([, k]) => k);
  if (sub === 'no') s.subtitles = [];
  else if (sub || langs.length) s.subtitles = langs;
  const inc = (cond: boolean, key: (typeof INCLUDES)[number]) => {
    if (cond && !s.includes.includes(key)) s.includes.push(key);
  };
  const ph = t(raw.photos).toLowerCase();
  inc(ph === 'photography', 'photo');
  inc(ph === 'thumbnail', 'thumbnail');
  const gr = t(raw.graphics).toLowerCase();
  inc(gr === 'graphic', 'graphics');
  inc(gr === 'animation', 'animation');
  inc(gr === 'ai', 'ai');
  const av = t(raw.actorVo).toLowerCase();
  inc(av === 'actor' || av === 'both', 'actor');
  inc(av === 'vo' || av === 'both', 'voice');
  inc(av === 'ai', 'ai');
  s.planningDate = isDateKey(t(raw.planning)) ? (t(raw.planning) as DateKey) : null;
  s.untilMonth = t(raw.period) ? untilMonthOf(t(raw.period), month) : null;
  return s;
}
